'use client';
import { Button } from '@/shared/ui/Button';
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock-upgrade';
import { motion } from 'framer-motion';
import Image from 'next/image';
import { FC, useEffect, useRef, useState } from 'react';
import InnerImageZoom from 'react-inner-image-zoom';
import 'react-inner-image-zoom/lib/InnerImageZoom/styles.css';
import YouTube from 'react-youtube';
import { EffectFade, FreeMode, Navigation, Pagination, Thumbs } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperType } from 'swiper/types';
import {
  cvaClose,
  cvaCloseIcon,
  cvaPagination,
  cvaRoot,
  cvaSlide,
  cvaSlideButton,
  cvaSlideButtonIcon,
  cvaSlideButtonWrapper,
  cvaSlideImage,
  cvaSlider,
  cvaSlideWrapper,
  cvaThumbs,
  cvaThumbsContainer,
  cvaThumbsSlide,
  cvaThumbsSlideImage,
  cvaVideo,
  cvaVideoIframe,
  cvaVideoThumb,
  cvaVideoThumbIcon,
  cvaVideoThumbImage,
  cvaVideoThumbTitle,
  cvaVideoThumbWrapper,
  cvaVideoWrapper,
} from './PopupGalleryStyles';
import IconArrowLeft from '/public/icons/arrow-right.svg';
import IconClose from '/public/icons/close.svg';
import IconPlay from '/public/icons/play.svg';

interface Props {
  initialIndex?: number;
  items: {
    thumb: string;
    full?: string;
    videoId?: string;
  }[];
  onClose: () => void;
}

const PopupGallery: FC<Props> = ({ initialIndex = 0, items, onClose }) => {
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const paginationRef = useRef<HTMLDivElement>(null);

  const rootRef = useRef<HTMLDivElement>(null);

  const [cursor, setCursor] = useState<'plus' | 'minus'>('plus');

  const [playVideoIndex, setPlayVideoIndex] = useState<number | null>(null);

  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);

  const onClickVideo = (index: number) => {
    setPlayVideoIndex(index);
  };

  useEffect(() => {
    if (rootRef.current) {
      disableBodyScroll(rootRef.current, {
        reserveScrollBarGap: true,
      });
    }

    return () => {
      clearAllBodyScrollLocks();
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        duration: 0.3,
        ease: 'easeInOut',
      }}
      className={cvaRoot()}
      data-lenis-prevent
      ref={rootRef}
    >
      <div className="absolute right-2.4 top-4 z-50">
        <Button
          type="button"
          className={cvaClose()}
          onClick={() => onClose()}
          color={'mediumGreyOnWhite'}
          hoverEffect={'highlight'}
          disableInViewAnimation
        >
          <IconClose className={cvaCloseIcon()} />
        </Button>
      </div>
      <div className={cvaThumbsContainer()}>
        <Swiper
          className={cvaThumbs()}
          initialSlide={initialIndex}
          direction={'vertical'}
          slidesPerView={'auto'}
          spaceBetween={8}
          freeMode
          modules={[Thumbs, FreeMode]}
          watchSlidesProgress
          onSwiper={setThumbsSwiper}
        >
          {items?.map((item, index) =>
            item.videoId?.length ? (
              <SwiperSlide className={cvaVideoThumbWrapper()} key={index}>
                <div className={cvaVideoThumb()} onClick={() => onClickVideo(index)}>
                  <Image src={item.thumb} width={56} height={56} alt="" className={cvaVideoThumbImage()} />
                  <IconPlay className={cvaVideoThumbIcon()} />
                </div>
                <div className={cvaVideoThumbTitle()}>видео-обзор</div>
              </SwiperSlide>
            ) : (
              <SwiperSlide key={index} className={cvaThumbsSlide()}>
                <Image src={item.thumb} width={56} height={56} alt={''} className={cvaThumbsSlideImage()} />
              </SwiperSlide>
            )
          )}
        </Swiper>
      </div>

      <div className={cvaSlideWrapper()}>
        {items.length > 1 ? (
          <div className="absolute h-full w-full">
            <Button
              type="button"
              className={cvaSlideButton()}
              ref={prevRef}
              rootWrapperClassName={cvaSlideButtonWrapper({
                direction: 'prev',
              })}
              color={'mediumGreyOnWhite'}
              hoverEffect={'highlight'}
            >
              <IconArrowLeft
                className={cvaSlideButtonIcon({
                  direction: 'prev',
                })}
              />
            </Button>
            <Button
              type="button"
              className={cvaSlideButton()}
              rootWrapperClassName={cvaSlideButtonWrapper({
                direction: 'next',
              })}
              ref={nextRef}
              color={'mediumGreyOnWhite'}
              hoverEffect={'highlight'}
            >
              <IconArrowLeft
                className={cvaSlideButtonIcon({
                  direction: 'next',
                })}
              />
            </Button>
          </div>
        ) : null}
        <div className={cvaPagination()} ref={paginationRef}></div>
        <Swiper
          modules={[EffectFade, Thumbs, Navigation, Pagination]}
          threshold={10}
          effect={'fade'}
          fadeEffect={{ crossFade: true }}
          watchOverflow
          loop
          initialSlide={initialIndex}
          navigation={{
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }}
          pagination={{
            el: paginationRef.current,
            clickable: true,
          }}
          onBeforeInit={(swiper: any) => {
            swiper.params.navigation.prevEl = prevRef.current;
            swiper.params.navigation.nextEl = nextRef.current;

            swiper.params.pagination.el = paginationRef.current;
          }}
          touchEventsTarget={'container'}
          thumbs={{ swiper: thumbsSwiper }}
          className={cvaSlider()}
          onSlideChange={(swiper) => {
            if (items[swiper.realIndex]?.videoId?.length) {
              setPlayVideoIndex(swiper.realIndex);
            } else {
              setPlayVideoIndex(null);
            }
          }}
        >
          {items?.map((item, index) =>
            item.videoId?.length ? (
              <SwiperSlide className={cvaVideoWrapper()} key={index}>
                {playVideoIndex === index ? (
                  <YouTube
                    videoId={item?.videoId}
                    className={cvaVideo()}
                    iframeClassName={cvaVideoIframe()}
                    opts={{
                      playerVars: {
                        autoplay: 1,
                      },
                    }}
                  />
                ) : null}
              </SwiperSlide>
            ) : (
              <SwiperSlide key={index} className={cvaSlide()}>
                <div>
                  <InnerImageZoom
                    src={item.thumb}
                    zoomScale={2}
                    className={cvaSlideImage({
                      cursor,
                    })}
                    hasSpacer
                    hideCloseButton={true}
                    hideHint={true}
                    afterZoomIn={() => setCursor('minus')}
                    afterZoomOut={() => setCursor('plus')}
                  />
                </div>
              </SwiperSlide>
            )
          )}
        </Swiper>
      </div>
    </motion.div>
  );
};

export default PopupGallery;
